import { useMemo, useState } from 'react'
import {
  DEFAULT_CHAT_MODEL_ID,
  LLM_CONFIG_CHAT_MODELS,
  type LlmConfigChatModelOption,
} from '../../lib/llmConfigModels'

export {
  CHAT_MODEL_CLAUDE_SONNET_45 as MODEL_CLAUDE,
  CHAT_MODEL_GPT4O as MODEL_GPT4O,
  CHAT_MODEL_GEMINI_25_PRO as MODEL_GEMINI,
} from '../../lib/llmConfigModels'

const MODEL_DESCRIPTIONS: Record<string, string> = {
  'gpt-4o': 'OpenAI · Versatile',
  'gpt-5-2025-08-07': 'OpenAI · Advanced',
  'gemini-2.5-pro': 'Google · Advanced reasoning',
  'claude-sonnet-4.5': 'Anthropic · Fast & balanced',
  'claude-sonnet-4.5-skills': 'Anthropic · Agent skills & code execution',
}

interface ModelPillProps {
  value: string
  onChange: (model: string) => void
  locked?: boolean
  onLockedClick?: () => void
  chatModelOptions?: LlmConfigChatModelOption[]
}

export function ModelPill({
  value,
  onChange,
  locked = false,
  onLockedClick,
  chatModelOptions,
}: ModelPillProps) {
  const models = useMemo(() => {
    const base =
      chatModelOptions && chatModelOptions.length > 0 ? chatModelOptions : [...LLM_CONFIG_CHAT_MODELS]
    return base.map((m) => ({
      ...m,
      description: MODEL_DESCRIPTIONS[m.id] ?? '',
    }))
  }, [chatModelOptions])

  const [open, setOpen] = useState(false)
  const selected =
    models.find((m) => m.id === value) ??
    models.find((m) => m.id === DEFAULT_CHAT_MODEL_ID) ??
    models[0]

  const handleClick = () => {
    if (locked) { onLockedClick?.(); return }
    setOpen(o => !o)
  }

  return (
    <>
      <div
        className={`v2-model-pill${locked ? ' locked' : ''}`}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-label="Select AI model"
        aria-haspopup="listbox"
        onKeyDown={e => e.key === 'Enter' && handleClick()}
      >
        <div className="v2-model-dot" />
        <span>{selected.label}</span>
        {locked
          ? <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><rect x="2.5" y="4.5" width="5" height="4" rx="0.8" stroke="currentColor" strokeWidth="1"/><path d="M3.5 4.5V3.5a1.5 1.5 0 013 0v1" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/></svg>
          : <svg className={`v2-chev${open ? ' open' : ''}`} width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
        }
        {open && !locked && (
          <>
            <div className="v2-model-backdrop" onClick={e => { e.stopPropagation(); setOpen(false) }} />
            <div className="v2-model-drop" role="listbox">
              {models.map(m => (
                <div
                  key={m.id}
                  className={`v2-model-opt${m.id === value ? ' selected' : ''}`}
                  onClick={e => { e.stopPropagation(); onChange(m.id); setOpen(false) }}
                  role="option"
                  aria-selected={m.id === value}
                >
                  <div className="v2-model-dot" />
                  <div>
                    <h4>{m.label}</h4>
                    <p>{m.description}</p>
                  </div>
                  {m.id === value && (
                    <svg className="v2-chk" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8l4 4 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                  )}
                </div>
              ))}
              <div className="v2-model-footer">Locks after your first message</div>
            </div>
          </>
        )}
      </div>
    </>
  )
}
